import * as yup from "yup";

import { fetchData } from "@utils/api";
import { APIMethods } from "@typeDefinitions";
import {
  ActionsDto,
  actionsSchema,
  ImageDto,
  NutrientDto,
  nutrientSchema,
} from "@client/common";
import { MetadataSchema, Metadata } from "@typeDefinitions";
import qs from "qs";
import { TagResourceDto, tagResourceSchema } from "@client/diets";

export interface FetchPatientSharedDietsParams {
  patientId: string;
  page?: number;
  perPage?: number;
  query?: string;
  status?: string;
  tags?: number[];
  nutrients?: string[];
  numberOfMeals?: string[];
}

export const fetchPatientSharedDiets = async ({
  patientId,
  ...params
}: FetchPatientSharedDietsParams): Promise<FetchPatientSharedDietsResponse> => {
  const query = qs.stringify(params);

  const response = await fetchData<FetchPatientSharedDietsResponse>(
    `/dietitian/patients/${patientId}/shared-content-diets/search?${query}`,
    APIMethods.GET,
  );
  return fetchPatientSharedDietsSchema.validate(response);
};

const imageSchema = yup.object().shape({
  url: yup.string().required(),
});

const dietSchema = yup.object().shape({
  id: yup.number().required(),
  clinicId: yup.number().nullable().default(null),
  namePl: yup.string().required(),
  nameEn: yup.string().nullable().default(null),
  mealsCount: yup.number().required(),
  hide: yup.boolean().required(),
  tags: yup.array(tagResourceSchema.required()).required(),
  macros: yup.array().of(nutrientSchema).required(),
  actions: actionsSchema.required(),
  favorite: yup.array().of(yup.number().required()).required(),
  image: imageSchema.nullable().default(null),
});

const statusSchema = yup.object().shape({
  id: yup.number().required(),
});

const dietItemSchema = yup.object().shape({
  diet: dietSchema.required(),
  status: statusSchema.required(),
  activeFrom: yup.string().required(),
});

const linksSchema = yup.object().shape({
  first: yup.string().required(),
  last: yup.string().required(),
  prev: yup.string().nullable().defined(),
  next: yup.string().nullable().defined(),
});

const fetchPatientSharedDietsSchema = yup.object().shape({
  data: yup.array().of(dietItemSchema).required(),
  links: linksSchema.required(),
  meta: MetadataSchema,
});

interface TagTranslation {
  lang: string;
  name: string;
}

interface Tag {
  id: number;
  system_id: number;
  clinic_id: number;
  tag_category_id: number;
  translations: TagTranslation[];
}

interface Macro {
  id: number;
  value: number;
}

interface Diet {
  id: number;
  clinicId: number | null;
  namePl: string;
  nameEn: string | null;
  mealsCount: number;
  hide: boolean;
  tags: TagResourceDto[];
  macros: NutrientDto[];
  favorite: number[];
  actions: ActionsDto;
  image: ImageDto | null;
}

interface Status {
  id: number;
}

interface DietItem {
  diet: Diet;
  status: Status;
  activeFrom: string;
}

interface Links {
  first: string;
  last: string;
  prev: string | null;
  next: string | null;
}

export interface FetchPatientSharedDietsResponse {
  data: DietItem[];
  links: Links;
  meta: Metadata;
}
